import type { Scene } from '../../types';

export function shortBackgroundLabel(path?: string): string {
  if (!path) return 'Aucun décor';
  const normalized = path.replace(/\\/g, '/');
  return normalized.split('/').pop() ?? path;
}

export function hasLocalBackground(scene: Scene): boolean {
  return scene.background !== undefined && scene.background !== '';
}

export function clearSceneBackground<T extends Scene>(scene: T): T {
  const { background: _background, backgroundLoop: _backgroundLoop, ...rest } = scene;
  return rest as T;
}

export function setSceneBackground<T extends Scene>(scene: T, path: string): T {
  const trimmed = path.trim();
  if (!trimmed) return clearSceneBackground(scene);
  return { ...scene, background: trimmed };
}

/** Teinte d'accent stable par chemin (distingue visuellement les backgrounds). */
export function backgroundAccentHue(path: string): number {
  let hash = 0;
  for (let i = 0; i < path.length; i++) {
    hash = path.charCodeAt(i) + ((hash << 5) - hash);
  }
  const hues = [205, 265, 320, 155, 35, 285, 175, 15];
  return hues[Math.abs(hash) % hues.length]!;
}

/** Section ouverte par une scène avec background local jusqu'au prochain background. */
export interface BackgroundSection {
  anchorSceneId: string;
  anchorIndex: number;
  endIndex: number;
  /** Nombre de scènes dans le groupe (ancre incluse). */
  sceneCount: number;
}

export function buildBackgroundSections(scenes: Scene[]): BackgroundSection[] {
  const sections: BackgroundSection[] = [];
  for (let i = 0; i < scenes.length; i++) {
    if (!hasLocalBackground(scenes[i]!)) continue;
    let end = i + 1;
    while (end < scenes.length && !hasLocalBackground(scenes[end]!)) {
      end++;
    }
    sections.push({
      anchorSceneId: scenes[i]!.id,
      anchorIndex: i,
      endIndex: end,
      sceneCount: end - i,
    });
  }
  return sections;
}

export function computeHiddenSceneIds(
  scenes: Scene[],
  sections: BackgroundSection[],
  collapsedAnchors: ReadonlySet<string>
): Set<string> {
  const hidden = new Set<string>();
  for (const section of sections) {
    if (!collapsedAnchors.has(section.anchorSceneId)) continue;
    for (let i = section.anchorIndex; i < section.endIndex; i++) {
      hidden.add(scenes[i]!.id);
    }
  }
  return hidden;
}

export function sectionForScene(
  sections: BackgroundSection[],
  sceneId: string
): BackgroundSection | undefined {
  return sections.find((s) => s.anchorSceneId === sceneId);
}

export function isSectionCollapsed(
  anchorSceneId: string,
  collapsedAnchors: ReadonlySet<string>
): boolean {
  return collapsedAnchors.has(anchorSceneId);
}

export function isBackgroundSectionMember(
  sections: BackgroundSection[],
  sceneId: string,
  scenes: Scene[]
): boolean {
  const idx = scenes.findIndex((s) => s.id === sceneId);
  if (idx < 0) return false;
  return sections.some((s) => idx >= s.anchorIndex && idx < s.endIndex);
}
